import { useEffect, useMemo, useState } from 'react';
import { api } from './api.js';
import { scoreHistory } from './score-history.js';

/** Page size of the summary view (GET /customers/{id}/runs?view=summary caps it at 400). */
export const HISTORY_PAGE = 400;
/** At most this many pages per load: 2000 runs, far more than any 30-day window holds. */
export const HISTORY_MAX_PAGES = 5;

/**
 * Every run of the window, newest first, following the cursor (ENTRAID-347, D1). One extra day is asked for so the
 * oldest local day of the window is whole whatever the time zone.
 */
async function loadWindow(customerId, days) {
  const runs = [];
  let cursor;
  for (let page = 0; page < HISTORY_MAX_PAGES; page += 1) {
    const r = await api.listRunHistory(customerId, { days: Math.min(days + 1, 400), view: 'summary', limit: HISTORY_PAGE, cursor });
    if (Array.isArray(r?.runs)) runs.push(...r.runs);
    cursor = r?.cursor;
    if (!cursor) break;
  }
  return runs;
}

/**
 * The onGuard score history for a customer (ENTRAID-347 redesign, B1 on D1): loads the runs of the last `days` days
 * (the summary view, so a run is a few hundred bytes) once per customer and reduces them to one point per local day
 * (lib/score-history.js). The window is by time, so manual runs never push older days out. `enabled` false skips
 * the request.
 *
 * @returns {{ status: 'idle'|'loading'|'ready'|'error'|'forbidden', runs: object[], history: ReturnType<typeof scoreHistory>, error: string|null }}
 */
export function useScoreHistory(customerId, { days = 30, enabled = true, reloadKey = 0 } = {}) {
  const [state, setState] = useState({ key: null, status: 'idle', runs: [], error: null });
  const key = `${customerId}|${days}|${reloadKey}`;

  useEffect(() => {
    if (!enabled || !customerId) return undefined;
    let live = true;
    setState((prev) => ({ key, status: 'loading', runs: prev.key?.startsWith(`${customerId}|`) ? prev.runs : [], error: null }));
    loadWindow(customerId, days).then(
      (runs) => { if (live) setState({ key, status: 'ready', runs, error: null }); },
      // A 403 means this viewer may not read collections (GET /customers/{id}/runs is operator or customer admin only):
      // nothing to show, never an error.
      (e) => { if (live) setState({ key, status: e?.status === 403 ? 'forbidden' : 'error', runs: [], error: e?.status === 403 ? null : (e?.message || 'The collection history could not be loaded.') }); },
    );
    return () => { live = false; };
  }, [customerId, days, enabled, key]);

  const history = useMemo(() => scoreHistory(state.runs, { days }), [state.runs, days]);
  return { status: enabled ? state.status : 'idle', runs: state.runs, history, error: state.error };
}
